import React from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import Toast from 'react-native-toast-message';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import AccessYourCard from '../../../components/rewards/AccessYourCard';
import AddToWallet from '../../../components/rewards/AddToWallet';
import InStoreCode from '../../../components/rewards/InStoreCode';
import MemberCard from '../../../components/rewards/MemberCard';
import OptOutCTA from '../../../components/rewards/OptOutCTA';
import {RewardsCardSkeleton, SkeletonGroup} from '../../../components/skeleton';
import Button from '../../../components/themed/Button';
import Text from '../../../components/themed/Text';
import ToastBottom from '../../../components/ToastBottom';
import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {layout, lineHeight, resolveWeight, type, universal} from '../../../design-system/tokens';
import logoImg from '../../../images/brand/header-logo.png';
import theme from '../../../style/theme';
import rewardsTheme from '../../../theme/rewardsTheme';
import {ACCOUNT_REWARDS} from '../../constants';
import SignUpPageLayout, {useWideLayout} from '../signUp/SignUpPageLayout';

/**
 * AccountRewardsScreen — DIS-1094 User Story
 *
 * Shows all states a logged-in rewards member can see:
 * 1. Loading — skeleton while TanStack Query fetches card data
 * 2. Has Rewards — full loyalty card with barcode, shortcode, wallet
 * 3. Error — toast notification (auto-dismiss), falls through to RewardsListScreen
 */

const meta = {
  title: 'Screens/Account/Rewards',
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <View style={styles.container}>
          <Story />
        </View>
      </ThemeProvider>
    ),
  ],
  parameters: {
    layout: 'fullscreen',
    // The ACCOUNT_REWARDS route's header in allStoresAppRoutes.tsx.
    siteFrame: {header: {back: true, hideDynamicHeader: false, source: ACCOUNT_REWARDS}},
  },
} satisfies Meta;

export default meta;

type Story = StoryObj<typeof meta>;

// ─── State 1: Loading (Skeleton) ────────────────────────────────────────────

export const Loading: Story = {
  render: () => (
    <ScrollView>
      <ContentView>
        <SkeletonGroup isLoading={true} skeleton={<RewardsCardSkeleton />}>
          <View />
        </SkeletonGroup>
      </ContentView>
    </ScrollView>
  ),
};

// ─── State 2: Has Rewards (Full Card) ───────────────────────────────────────

// DIS-2006: desktop web mirrors the screen's centred card (the success-screen
// layout); narrow widths keep the full-width column.
const HasRewardsView = () => {
  const {isDesktopCard: isWide, onLayout} = useWideLayout();
  const memberCard = (
    <MemberCard
      logoImg={logoImg}
      cardNumber="123456789"
      name="Kerry Smith"
      memberSince="2024-03-15"
    />
  );
  const greeting =
    'Congrats, Kerry! You are now a part of our rewards program! Show this barcode at checkout to collect awesome rewards!';

  if (isWide) {
    return (
      <ScrollView style={{backgroundColor: universal.white}}>
        <SignUpPageLayout
          centred
          isWide={isWide}
          onLayout={onLayout}
          testID="account-rewards-has-rewards-screen"
          heading={
            <Text style={[type.heading.h1, lineHeight.heading.h1, wideStyles.heading]}>
              Your rewards card
            </Text>
          }
          intro={
            <Text style={[type.paragraph.p1, lineHeight.paragraph.p1, wideStyles.centred]}>
              {greeting}
            </Text>
          }
          main={
            <>
              {memberCard}
              <Text style={[resolveWeight('semiBold'), wideStyles.tempHeading]} variant="heading2">
                Your temporary rewards code
              </Text>
              <Text variant="body2" style={wideStyles.tempBody}>
                Want to keep ahold of your phone? Give this code at checkout instead.
              </Text>
              <InStoreCode inStoreCode="ABC123" />
            </>
          }
          aside={
            <View style={wideStyles.aside}>
              <AccessYourCard />
              <OptOutCTA onOptOut={() => {}} />
            </View>
          }
        />
      </ScrollView>
    );
  }

  return (
    <ScrollView>
      <ContentView testID="account-rewards-has-rewards-screen" onLayout={onLayout}>
        <Text variant="heading1">Your rewards card</Text>
        <Text variant="body2">{greeting}</Text>
        <View style={{gap: 8, marginTop: 24}}>
          {memberCard}
          <AddToWallet walletUrl="https://example.com/wallet" />
        </View>
        <Text style={{fontWeight: 600, marginTop: 50}} variant="heading2">
          Your temporary rewards code
        </Text>
        <Text variant="body2" style={{marginTop: 8}}>
          Want to keep ahold of your phone? Give this code at checkout instead.
        </Text>
        <InStoreCode inStoreCode="ABC123" />
        <View style={{height: 50}} />
        <AccessYourCard />
        <View style={{height: 50}} />
        <OptOutCTA onOptOut={() => {}} />
        <View style={{height: 50}} />
      </ContentView>
    </ScrollView>
  );
};

export const HasRewards: Story = {
  render: () => <HasRewardsView />,
};

// ─── State 3: Loading → Success Transition ──────────────────────────────────

export const LoadingToSuccess: Story = {
  render: () => {
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
      const timer = setTimeout(() => setIsLoading(false), 2000);
      return () => clearTimeout(timer);
    }, []);

    return (
      <ScrollView>
        <ContentView>
          <SkeletonGroup isLoading={isLoading} skeleton={<RewardsCardSkeleton />}>
            <View>
              <Text variant="heading1">Your rewards card</Text>
              <Text variant="body2">
                Congrats, Kerry! You are now a part of our rewards program!
              </Text>
              <View style={{gap: 8, marginTop: 24}}>
                <MemberCard
                  logoImg={logoImg}
                  cardNumber="123456789"
                  name="Kerry Smith"
                  memberSince="2024-03-15"
                />
                <AddToWallet walletUrl="https://example.com/wallet" />
              </View>
              <Text style={{fontWeight: 600, marginTop: 50}} variant="heading2">
                Your temporary rewards code
              </Text>
              <Text variant="body2" style={{marginTop: 8}}>
                Want to keep ahold of your phone? Give this code at checkout instead.
              </Text>
              <InStoreCode inStoreCode="ABC123" />
            </View>
          </SkeletonGroup>
        </ContentView>
      </ScrollView>
    );
  },
};

// ─── State 4: Error Toast ───────────────────────────────────────────────────

export const ErrorToast: Story = {
  render: () => {
    const triggerToast = () => {
      Toast.show({
        type: ERROR_MESSAGE,
        text1: "Couldn't load your rewards card right now.",
      });
    };

    return (
      <View style={{flex: 1, padding: 20}}>
        <Text variant="heading2" style={{marginBottom: 8}}>
          Error Toast Preview
        </Text>
        <Text variant="body2" style={{marginBottom: 24, color: '#666'}}>
          When rewards card data fails to load, a toast notification appears briefly and
          auto-dismisses. The user lands on the rewards marketing screen underneath.
        </Text>
        <View style={{width: 250}}>
          <Button title="SIMULATE ERROR" onPress={triggerToast} />
        </View>
        <ToastBottom type={ERROR_MESSAGE} />
      </View>
    );
  },
};

// ─── All States Side by Side ────────────────────────────────────────────────

export const AllStates: Story = {
  render: () => (
    <ScrollView>
      <View style={styles.allStatesContainer}>
        <View style={styles.stateSection}>
          <Text variant="heading2" style={styles.stateLabel}>
            1. Loading
          </Text>
          <View style={styles.stateCard}>
            <SkeletonGroup isLoading={true} skeleton={<RewardsCardSkeleton />}>
              <View />
            </SkeletonGroup>
          </View>
        </View>

        <View style={styles.stateSection}>
          <Text variant="heading2" style={styles.stateLabel}>
            2. Has Rewards
          </Text>
          <View style={styles.stateCard}>
            <MemberCard
              logoImg={logoImg}
              cardNumber="123456789"
              name="Kerry Smith"
              memberSince="2024-03-15"
            />
            <View style={{marginTop: 8}}>
              <InStoreCode inStoreCode="ABC123" />
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  ),
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  allStatesContainer: {
    padding: 16,
    gap: 32,
  },
  stateSection: {
    gap: 8,
  },
  stateLabel: {
    color: '#666',
    fontSize: 14,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  stateCard: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 12,
    padding: 16,
    backgroundColor: '#fff',
  },
});

// The screen's desktop-card styles (AccountRewardsScreen/index.tsx).
const wideStyles = StyleSheet.create({
  heading: {color: theme.colour.text.heading.primary, textAlign: 'center'},
  centred: {textAlign: 'center'},
  tempHeading: {marginTop: layout.desktop.stackSection, textAlign: 'center'},
  tempBody: {marginTop: layout.desktop.stackRelated, textAlign: 'center'},
  aside: {gap: layout.desktop.stackGroup},
});
